/**
 * useAsyncOperation Hook
 *
 * Custom hook for handling async operations with loading states, error handling, and retry logic.
 * Provides a clean API for managing async state throughout the application.
 */

import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Default options for async operations
 */
const DEFAULT_OPTIONS = {
  immediate: false,
  retries: 0,
  retryDelay: 1000,
  timeout: 30000,
  onSuccess: null,
  onError: null,
  onFinally: null,
};

/**
 * Main useAsyncOperation hook
 */
export function useAsyncOperation(asyncFunction, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // State
  const [state, setState] = useState({
    data: null,
    error: null,
    loading: false,
    executed: false,
  });

  // Refs for cleanup and cancellation
  const mountedRef = useRef(true);
  const timeoutRef = useRef(null);
  const retryCountRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Execute the async operation
   */
  const execute = useCallback(
    async (...args) => {
      if (!mountedRef.current) return;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set loading state
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        executed: true,
      }));

      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        timeoutRef.current = setTimeout(() => {
          reject(new Error(`Operation timed out after ${opts.timeout}ms`));
        }, opts.timeout);
      });

      try {
        // Race between the async function and timeout
        const result = await Promise.race([
          asyncFunction(...args),
          timeoutPromise,
        ]);

        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Update state if still mounted
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            data: result,
            loading: false,
            error: null,
          }));

          // Call success callback
          if (opts.onSuccess) {
            opts.onSuccess(result);
          }
        }

        // Reset retry count on success
        retryCountRef.current = 0;

        return result;
      } catch (error) {
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Check if we should retry
        if (retryCountRef.current < opts.retries) {
          retryCountRef.current++;

          // Wait for retry delay
          await new Promise((resolve) => {
            setTimeout(resolve, opts.retryDelay * retryCountRef.current);
          });

          // Retry the operation
          return execute(...args);
        }

        // Update error state if still mounted
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error,
          }));

          // Call error callback
          if (opts.onError) {
            opts.onError(error);
          }
        }

        throw error;
      } finally {
        // Call finally callback
        if (opts.onFinally && mountedRef.current) {
          opts.onFinally();
        }
      }
    },
    [asyncFunction, opts]
  );

  /**
   * Reset the state
   */
  const reset = useCallback(() => {
    if (!mountedRef.current) return;

    setState({
      data: null,
      error: null,
      loading: false,
      executed: false,
    });

    retryCountRef.current = 0;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Retry the last operation
   */
  const retry = useCallback(() => {
    retryCountRef.current = 0;
    return execute();
  }, [execute]);

  // Execute immediately if requested
  useEffect(() => {
    if (opts.immediate) {
      execute();
    }
  }, [execute, opts.immediate]);

  return {
    ...state,
    execute,
    reset,
    retry,
    isIdle: !state.executed,
    isLoading: state.loading,
    isSuccess: !state.loading && !state.error && state.data !== null,
    isError: !state.loading && state.error !== null,
  };
}

/**
 * Hook for handling multiple async operations
 */
export function useAsyncOperations(operations = {}, options = {}) {
  const [globalState, setGlobalState] = useState({
    loading: false,
    errors: {},
    executed: {},
  });

  const operationHooks = {};
  const operationStates = {};

  // Create individual hooks for each operation
  Object.keys(operations).forEach((key) => {
    operationHooks[key] = useAsyncOperation(operations[key], {
      ...options,
      onError: (error) => {
        setGlobalState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [key]: error },
        }));
        if (options.onError) options.onError(key, error);
      },
      onSuccess: (data) => {
        setGlobalState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [key]: null },
        }));
        if (options.onSuccess) options.onSuccess(key, data);
      },
    });

    operationStates[key] = operationHooks[key];
  });

  // Calculate global loading state
  const isGloballyLoading = Object.values(operationHooks).some(
    (hook) => hook.loading
  );

  // Update global loading state
  useEffect(() => {
    setGlobalState((prev) => ({ ...prev, loading: isGloballyLoading }));
  }, [isGloballyLoading]);

  /**
   * Execute all operations
   */
  const executeAll = useCallback(
    async (args = {}) => {
      const promises = Object.keys(operations).map((key) => {
        const operationArgs = args[key] || [];
        return operationHooks[key].execute(...operationArgs);
      });

      return Promise.allSettled(promises);
    },
    [operations, operationHooks]
  );

  /**
   * Reset all operations
   */
  const resetAll = useCallback(() => {
    Object.values(operationHooks).forEach((hook) => hook.reset());
    setGlobalState({
      loading: false,
      errors: {},
      executed: {},
    });
  }, [operationHooks]);

  return {
    operations: operationStates,
    globalState,
    executeAll,
    resetAll,
    isGloballyLoading,
  };
}

/**
 * Hook specifically for API operations
 */
export function useApiOperation(apiFunction, options = {}) {
  return useAsyncOperation(apiFunction, {
    retries: 2,
    retryDelay: 1000,
    timeout: 10000,
    ...options,
    onError: (error) => {
      // Enhance error with API-specific information
      if (error.response) {
        error.status = error.response.status;
        error.statusText = error.response.statusText;
      }

      if (options.onError) {
        options.onError(error);
      }
    },
  });
}

/**
 * Hook for metadata loading operations
 */
export function useMetadataOperation(metadataFunction, options = {}) {
  return useAsyncOperation(metadataFunction, {
    retries: 1,
    retryDelay: 500,
    timeout: 15000,
    ...options,
    onError: (error) => {
      // Add metadata-specific error context
      error.context = "metadata_loading";

      if (options.onError) {
        options.onError(error);
      }
    },
  });
}

/**
 * Hook for form submission operations
 */
export function useFormSubmission(submitFunction, options = {}) {
  const operation = useAsyncOperation(submitFunction, {
    retries: 0, // Don't retry form submissions by default
    timeout: 30000,
    ...options,
  });

  // Add form-specific helpers
  const submitWithValidation = useCallback(
    async (formData, validator) => {
      try {
        // Run validation if provided
        if (validator) {
          const validationResult = await validator(formData);
          if (!validationResult.valid) {
            throw new Error(
              `Validation failed: ${validationResult.errors.join(", ")}`
            );
          }
        }

        return await operation.execute(formData);
      } catch (error) {
        throw error;
      }
    },
    [operation]
  );

  return {
    ...operation,
    submitWithValidation,
    isSubmitting: operation.loading,
    hasSubmitted: operation.executed,
  };
}

/**
 * Hook for paginated data loading
 */
export function usePaginatedOperation(fetchFunction, options = {}) {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: options.limit || 20,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });

  const operation = useAsyncOperation(
    async (page, limit) => {
      const result = await fetchFunction(page, limit);

      // Update pagination state
      if (result.pagination) {
        setPagination((prev) => ({
          ...prev,
          ...result.pagination,
          page,
          limit,
        }));
      }

      return result;
    },
    {
      immediate: options.immediate !== false,
      ...options,
    }
  );

  const loadPage = useCallback(
    (page) => {
      return operation.execute(page, pagination.limit);
    },
    [operation, pagination.limit]
  );

  const nextPage = useCallback(() => {
    if (pagination.hasNext) {
      return loadPage(pagination.page + 1);
    }
  }, [loadPage, pagination]);

  const prevPage = useCallback(() => {
    if (pagination.hasPrev) {
      return loadPage(pagination.page - 1);
    }
  }, [loadPage, pagination]);

  const refresh = useCallback(() => {
    return loadPage(pagination.page);
  }, [loadPage, pagination.page]);

  return {
    ...operation,
    pagination,
    loadPage,
    nextPage,
    prevPage,
    refresh,
  };
}

// Export all hooks
export default useAsyncOperation;
